import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "@hookform/error-message";
import { useSelector } from "react-redux";
import addressValidation from "../Validation/AddressValidaton";
import useMakeRequest from "../utils/ApiHelper";
import { ApiTypes } from "../constants/Constants";
import { ApiConstants } from "../constants/ApiConstants";
import BrandedLoaderOverlay from "../component/layout/BrandedLoaderOverlay";
import { useResourceEverLoaded } from "../component/layout/useSmartBrandedLoader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LocationInput from "../component/address/LocationInput";
import { LanguageConstants } from "../constants/LanguageConstants";
import { attachAddressLine2ForApi } from "../utils/addressPayload";
import { Routes } from "../constants/RouteConstants";
import { isProfileBusinessAccount, isProfileIndividualAccount } from "../utils/profileAccountType";

interface FormInputs {
  street_line: string;
  appartment: string;
  state: string;
  city: string;
  zip_code: string;
  loaction_type: string;
  instructions: string;
}

function UpdateAddress() {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: { address?: any } };
  const { id } = useParams();
  const detailRequest = useMakeRequest();
  const updateRequest = useMakeRequest();
  const shippingListRequest = useMakeRequest();
  const [shippingId, setShippingId] = useState("");
  const [isPrimaryAddress, setIsPrimaryAddress] = useState(false);
  const [isBillingSame, setIsBillingSame] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [country, setCountry] = useState("");
  const profileState = useSelector((state: any) => state?.ProfileState);
  const isBusiness = isProfileBusinessAccount(profileState);
  const isIndividual = isProfileIndividualAccount(profileState);
  /** Match NewAddressPage: business = billing only; individual = primary + billing; other = primary only. */
  const showBillingSameCheckbox = isBusiness || isIndividual;
  const showPrimaryAddressCheckbox = !isBusiness;
  const billingSameFromApiRef = useRef(false);

  const addressId = state?.address?.id ?? id;

  const shippingRows = useMemo(() => {
    const raw = shippingListRequest?.data?.data;
    const rows = Array.isArray(raw) ? raw : [];
    return rows
      .map((row: any) => {
        const d = row?.shipping_details;
        const refId = d?.ref_id ?? row?.ref_id;
        const name = d?.name ?? row?.name;
        if (refId == null || name == null) return null;
        return { refId: String(refId), name: String(name) };
      })
      .filter(Boolean) as { refId: string; name: string }[];
  }, [shippingListRequest?.data?.data]);

  const { register, watch, setValue, reset, formState: { errors }, handleSubmit } = useForm<FormInputs>({
    mode: "onChange",
    resolver: yupResolver(addressValidation),
    defaultValues: {
      street_line: "",
      appartment: "",
      state: "",
      city: "",
      zip_code: "",
      loaction_type: "1",
      instructions: "",
    },
  });

  const applyAddressData = useCallback(
    (addressData: any) => {
      if (!addressData) return;
      reset({
        street_line: addressData?.street_line || "",
        appartment: addressData?.appartment || "",
        state: addressData?.state || "",
        city: addressData?.city || "",
        zip_code: addressData?.zip_code || "",
        loaction_type: String(addressData?.loaction_type || "1"),
        instructions: addressData?.instructions || "",
      });
      setLatitude(addressData?.lat ?? null);
      setLongitude(addressData?.long ?? null);
      setCountry(addressData?.country || "");

      const sid = addressData?.shipping_id;
      setShippingId(sid != null && String(sid).trim() !== "" ? String(sid) : "");
      setIsPrimaryAddress(
        Number(addressData?.is_primary) === 1 ||
          addressData?.is_primary === true ||
          String(addressData?.is_primary) === "1",
      );
      const billingSameRaw =
        Number(addressData?.billing_same_as_shipping) === 1 ||
        addressData?.billing_same_as_shipping === true ||
        String(addressData?.billing_same_as_shipping) === "1";
      billingSameFromApiRef.current = billingSameRaw;
      setIsBillingSame(showBillingSameCheckbox && billingSameRaw);
    },
    [reset, showBillingSameCheckbox],
  );

  useEffect(() => {
    if (!showBillingSameCheckbox) {
      setIsBillingSame(false);
      return;
    }
    setIsBillingSame(billingSameFromApiRef.current);
  }, [showBillingSameCheckbox]);

  useEffect(() => {
    shippingListRequest.makeRequest({
      url: ApiConstants?.ShippingList,
      toastStatus: false,
    });
  }, []);

  useEffect(() => {
    if (state?.address) {
      applyAddressData(state.address);
      return;
    }
    if (id) {
      detailRequest.makeRequest({
        payload: { address_id: id },
        url: ApiConstants?.AddressDetail,
        toastStatus: false,
      });
    }
  }, [applyAddressData, id, state?.address]);

  useEffect(() => {
    if (!detailRequest?.status) return;
    const raw = detailRequest?.data?.data;
    const detailData = raw?.rows?.[0] ?? raw ?? null;
    applyAddressData(detailData);
  }, [applyAddressData, detailRequest?.data, detailRequest?.status]);

  useEffect(() => {
    if (updateRequest?.status) {
      navigate(Routes.AddressList);
    }
  }, [navigate, updateRequest?.status]);

  const handleLocationSuccess = (result: any) => {
    setLatitude(result?.latitudeLongitude?.lat ?? null);
    setLongitude(result?.latitudeLongitude?.lng ?? null);
    setValue("street_line", result?.formattedAddress || "");
    setValue("city", result?.city || "");
    setValue("state", result?.state || "");
    setCountry(result?.country || "");
    if (result?.zip_code) {
      setValue("zip_code", result.zip_code);
    }
  };

  const onSubmit = (body: FormInputs) => {
    const shipping_id_num =
      shippingId != null && String(shippingId).trim() !== "" ? Number(shippingId) : null;
    updateRequest.makeRequest({
      payload: attachAddressLine2ForApi({
        ...body,
        country,
        lat: latitude,
        long: longitude,
        address_id: addressId,
        shipping_id:
          Number.isFinite(shipping_id_num as number) && (shipping_id_num as number) > 0 ? shipping_id_num : null,
        is_primary: isPrimaryAddress ? 1 : 0,
        billing_same_as_shipping: showBillingSameCheckbox && isBillingSame ? 1 : 0,
      }),
      type: ApiTypes.POST,
      url: ApiConstants?.AddressUpdate,
      toastStatus: true,
    });
  };

  const detailEverLoaded = useResourceEverLoaded(detailRequest?.data?.data != null);
  const shippingEverLoaded = useResourceEverLoaded(shippingListRequest?.data?.data != null);
  const showAddressFormLoader =
    updateRequest.loading ||
    (detailRequest.loading && !detailEverLoaded) ||
    (shippingListRequest.loading && !shippingEverLoaded);

  return (
    <>
      <BrandedLoaderOverlay active={showAddressFormLoader} />
      <div className="head-space">
        <div className="inner-content">
          <div className="container">
            <div className="address-book-shell address-book-shell--form">
              <span className="address-book-badge">Address Book</span>
              <form onSubmit={handleSubmit(onSubmit)} className="address-form-page">
                <div className="address-form-section">
                  <h2 className="address-form-section__heading">{LanguageConstants?.UPDATEADDRES}</h2>
                  <div className="row g-3">
                    <div className="col-lg-4">
                      <div className="address-form-field">
                        <label className="address-form-label">Address Line 1</label>
                        <LocationInput
                          id="update-address-line-1"
                          className="form-control address-form-input"
                          onSuccess={handleLocationSuccess}
                          value={watch("street_line")}
                          placeholder="Enter address"
                          onChange={(e) => setValue("street_line", e?.target?.value)}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="street_line"
                          render={({ message }) => <p className="text-error-new">{message}</p>}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="address-form-field">
                        <label className="address-form-label">Address Line 2</label>
                        <input
                          type="text"
                          className="form-control address-form-input"
                          placeholder="Enter address"
                          {...register("appartment")}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="appartment"
                          render={({ message }) => <p className="text-error-new">{message}</p>}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="address-form-field">
                        <label className="address-form-label">{LanguageConstants?.INSTRUCTIONS}</label>
                        <input
                          type="text"
                          className="form-control address-form-input"
                          placeholder="Enter instructions"
                          {...register("instructions")}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="instructions"
                          render={({ message }) => <p className="text-error-new">{message}</p>}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="address-form-divider"></div>

                <div className="address-form-section">
                  <h2 className="address-form-section__heading">Type of Residence</h2>
                  <p className="address-form-section__subheading">Select Residence Type</p>
                  <div className="address-form-options">
                    <label className="address-form-option">
                      <input type="radio" value="1" {...register("loaction_type")} />
                      <span>{LanguageConstants?.RESIDENTIAL}</span>
                    </label>
                    <label className="address-form-option">
                      <input type="radio" value="2" {...register("loaction_type")} />
                      <span>{LanguageConstants?.ACCOUNT_TYPE_BUSINESS || "Business"}</span>
                    </label>
                    <label className="address-form-option">
                      <input type="radio" value="3" {...register("loaction_type")} />
                      <span>{LanguageConstants?.INDUSTRIAL || "Industrial"}</span>
                    </label>
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="loaction_type"
                    render={({ message }) => <p className="text-error-new mt-2">{message}</p>}
                  />
                </div>

                <div className="address-form-divider"></div>

                <div className="address-form-section">
                  <h2 className="address-form-section__heading">Shipping Method</h2>
                  <p className="address-form-section__subheading">Select Shipping Method</p>
                  <div className="address-form-options">
                    {shippingRows.length > 0 ? (
                      shippingRows.map((item) => (
                        <label className="address-form-option" key={item.refId}>
                          <input
                            type="radio"
                            name="shipping_id"
                            value={item.refId}
                            checked={shippingId === item.refId}
                            onChange={(e) => setShippingId(e.target.value)}
                          />
                          <span>{item.name}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-muted small mb-0">Loading shipping methods…</p>
                    )}
                  </div>
                </div>
                <br />

                <div className="address-form-checks">
                  {showPrimaryAddressCheckbox ? (
                  <label className="address-form-check">
                    <input
                      type="checkbox"
                      checked={isPrimaryAddress}
                      onChange={(e) => setIsPrimaryAddress(e.target.checked)}
                    />
                    <span>Set as Primary Address</span>
                  </label>
                  ) : null}
                  {showBillingSameCheckbox ? (
                  <label className="address-form-check">
                    <input
                      type="checkbox"
                      checked={isBillingSame}
                      onChange={(e) => setIsBillingSame(e.target.checked)}
                    />
                    <span>Billing Address is the same as shipping</span>
                  </label>
                  ) : null}
                </div>

                <div className="address-form-actions">
                  <button type="submit" className="manual-quote-btn com-btn rounded-pill">
                    {LanguageConstants?.SAVE}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdateAddress;
